import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * Adaptador de data em português: os campos de calendário passam a ler e escrever `dd/mm/aaaa`,
 * como o resto da aplicação já exibia (grades, faixa de filtros, relatórios em PDF).
 *
 * <p>Exibir no formato certo é só metade: o `NativeDateAdapter` interpreta o que se digita com
 * `Date.parse`, que segue a convenção americana — `Date.parse('01/08/2026')` devolve <b>8 de
 * janeiro</b>, não 1º de agosto. Sem sobrescrever a leitura, o campo mostraria uma data e
 * guardaria outra, calado. Daí este adaptador existir em vez de apenas trocar o locale.</p>
 */
@Injectable()
export class AdaptadorDataBr extends NativeDateAdapter {

  /** `dd/mm/aaaa`, aceitando também um dígito por campo e `-` ou `.` como separador. */
  private static readonly FORMATO = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/;

  /**
   * Texto digitado → data. Devolve `null` para o que não for uma data completa em `dd/mm/aaaa`:
   * é o que faz o campo se marcar como inválido em vez de assumir um dia qualquer.
   */
  override parse(value: unknown): Date | null {
    if (typeof value !== 'string') {
      return super.parse(value);
    }
    const partes = AdaptadorDataBr.FORMATO.exec(value.trim());
    if (!partes) {
      return null;
    }
    const [, dia, mes, ano] = partes;
    return this.dataValida(Number(ano), Number(mes), Number(dia));
  }

  /**
   * A data, ou `null` quando o dia não existe no mês.
   *
   * O construtor do `Date` normaliza sem reclamar — 31/02 vira 3 de março —, o que aceitaria uma
   * data inexistente como se fosse válida. Conferir os componentes depois é o que detecta isso.
   */
  private dataValida(ano: number, mes: number, dia: number): Date | null {
    const data = new Date(ano, mes - 1, dia);
    const bate = data.getFullYear() === ano
      && data.getMonth() === mes - 1
      && data.getDate() === dia;
    return bate ? data : null;
  }
}
